import * as React from 'react';
import classNames from 'classnames';
import Icon from 'acud-icon';
import Tooltip from '../../tooltip';
import { getColumnKey, getColumnPos, renderColumnTitle } from '../util';
const UpSvg = () => <svg width="16px" height="8px" viewBox="0 0 16 8" fill="currentColor">
    <path d="M9.14700743,0.645886681 C9.34257881,0.450934266 9.6591609,0.45143604 9.85411332,0.647007426 C10.0490657,0.842578811 10.048564,1.1591609 9.85299257,1.35411332 L7.207,3.991 L9.85411155,6.64700565 C10.0274035,6.82084602 10.0462629,7.09030055 9.91095914,7.28495516 L9.85299435,7.35411155 C9.65742394,7.54906495 9.34084184,7.54856476 9.14588845,7.35299435 L6.14588845,4.34349955 C5.95093436,4.14792845 5.95143535,3.83134499 6.14700743,3.63639189 L9.14700743,0.645886681 Z" id="1-L" transform="translate(8.000000, 4.000000) rotate(90.000000) translate(-8.000000, -4.000000) "></path>
</svg>;
const DownSvg = () => <svg width="16px" height="8px" viewBox="0 0 16 8" fill="currentColor">
    <path d="M9.14700743,0.645886681 C9.34257881,0.450934266 9.6591609,0.45143604 9.85411332,0.647007426 C10.0490657,0.842578811 10.048564,1.1591609 9.85299257,1.35411332 L7.207,3.991 L9.85411155,6.64700565 C10.0274035,6.82084602 10.0462629,7.09030055 9.91095914,7.28495516 L9.85299435,7.35411155 C9.65742394,7.54906495 9.34084184,7.54856476 9.14588845,7.35299435 L6.14588845,4.34349955 C5.95093436,4.14792845 5.95143535,3.83134499 6.14700743,3.63639189 L9.14700743,0.645886681 Z" id="1-L" transform="translate(8.000000, 4.000000) rotate(-90.000000) translate(-8.000000, -4.000000) "></path>
</svg>;
const ASCEND = 'ascend';
const DESCEND = 'descend';
function getMultiplePriority(column) {
    if (typeof column.sorter === 'object' && typeof column.sorter.multiple === 'number') {
        return column.sorter.multiple;
    }
    return false;
}
function getSortFunction(sorter) {
    if (typeof sorter === 'function') {
        return sorter;
    }
    if (sorter && typeof sorter === 'object' && sorter.compare) {
        return sorter.compare;
    }
    return false;
}
function nextSortDirection(sortDirections, current) {
    if (!current) {
        return sortDirections[0];
    }
    return sortDirections[sortDirections.indexOf(current) + 1];
}
function collectSortStates(columns, init, pos) {
    let sortStates = [];
    function pushState(column, columnPos) {
        sortStates.push({
            column,
            key: getColumnKey(column, columnPos),
            multiplePriority: getMultiplePriority(column),
            sortOrder: column.sortOrder
        });
    }
    (columns || []).forEach((column, index) => {
        const columnPos = getColumnPos(index, pos);
        if (column.children) {
            if ('sortOrder' in column) {
                // Controlled
                pushState(column, columnPos);
            }
            sortStates = [
                ...sortStates,
                ...collectSortStates(column.children, init, columnPos)
            ];
        }
        else if (column.sorter) {
            if ('sortOrder' in column) {
                // Controlled
                pushState(column, columnPos);
            }
            else if (init && column.defaultSortOrder) {
                // Default sorter
                sortStates.push({
                    column,
                    key: getColumnKey(column, columnPos),
                    multiplePriority: getMultiplePriority(column),
                    sortOrder: column.defaultSortOrder
                });
            }
        }
    });
    return sortStates;
}
function injectSorter(prefixCls, columns, sorterSates, triggerSorter, defaultSortDirections, tableLocale, tableShowSorterTooltip, pos) {
    return (columns || []).map((column, index) => {
        const columnPos = getColumnPos(index, pos);
        let newColumn = column;
        if (newColumn.sorter) {
            const sortDirections = newColumn.sortDirections || defaultSortDirections;
            const showSorterTooltip = newColumn.showSorterTooltip === undefined
                ? tableShowSorterTooltip
                : newColumn.showSorterTooltip;
            const columnKey = getColumnKey(newColumn, columnPos);
            const sorterState = sorterSates.find(({ key }) => key === columnKey);
            const sorterOrder = sorterState ? sorterState.sortOrder : null;
            const nextSortOrder = nextSortDirection(sortDirections, sorterOrder);
            const upNode = (<Icon width={16} height={8} component={UpSvg} className={classNames(`${prefixCls}-column-sorter-icon`, `${prefixCls}-column-sorter-up`, {
                    active: sorterOrder === ASCEND
                })}/>);
            const downNode = (<Icon width={16} height={8} component={DownSvg} className={classNames(`${prefixCls}-column-sorter-icon`, `${prefixCls}-column-sorter-down`, {
                    active: sorterOrder === DESCEND
                })}/>);
            const { cancelSort, triggerAsc, triggerDesc } = tableLocale || {};
            let sortTip = cancelSort;
            if (nextSortOrder === DESCEND) {
                sortTip = triggerDesc;
            }
            else if (nextSortOrder === ASCEND) {
                sortTip = triggerAsc;
            }
            const tooltipProps = typeof showSorterTooltip === 'object' ? showSorterTooltip : { title: sortTip };
            newColumn = Object.assign(Object.assign({}, newColumn), { className: classNames(newColumn.className, { [`${prefixCls}-column-sort`]: sorterOrder }), title: (renderProps) => {
                    const renderSortTitle = (<div className={`${prefixCls}-column-sorters`}>
                            <span className={`${prefixCls}-column-title`}>
                                {renderColumnTitle(column.title, renderProps)}
                            </span>
                            {showSorterTooltip
                            ? (<Tooltip {...tooltipProps}>
                                        <span className={classNames(`${prefixCls}-column-sorter`, {
                                    [`${prefixCls}-column-sorter-full`]: !!(upNode && downNode),
                                    [`${prefixCls}-column-sorter-default`]: sorterOrder !== ASCEND && sorterOrder !== DESCEND
                                })} onClick={() => {
                                    triggerSorter({
                                        column,
                                        key: columnKey,
                                        sortOrder: nextSortOrder,
                                        multiplePriority: getMultiplePriority(column)
                                    });
                                }}>
                                            <span className={`${prefixCls}-column-sorter-inner`}>
                                                {upNode}{downNode}
                                            </span>
                                        </span>
                                    </Tooltip>)
                            : (<span className={classNames(`${prefixCls}-column-sorter`, {
                                    [`${prefixCls}-column-sorter-full`]: !!(upNode && downNode),
                                    [`${prefixCls}-column-sorter-default`]: sorterOrder !== ASCEND && sorterOrder !== DESCEND
                                })} onClick={(event) => {
                                    triggerSorter({
                                        column,
                                        key: columnKey,
                                        sortOrder: nextSortOrder,
                                        multiplePriority: getMultiplePriority(column)
                                    });
                                    event.stopPropagation();
                                }}>
                                        <span className={`${prefixCls}-column-sorter-inner`}>
                                            {upNode}{downNode}
                                        </span>
                                    </span>)}
                        </div>);
                    return renderSortTitle;
                }, onHeaderCell: col => {
                    const cell = (column.onHeaderCell && column.onHeaderCell(col)) || {};
                    cell.className = classNames(cell.className, `${prefixCls}-column-has-sorters`);
                    return cell;
                } });
        }
        if ('children' in newColumn) {
            newColumn = Object.assign(Object.assign({}, newColumn), { children: injectSorter(prefixCls, newColumn.children, sorterSates, triggerSorter, defaultSortDirections, tableLocale, tableShowSorterTooltip, columnPos) });
        }
        return newColumn;
    });
}
function stateToInfo(sorterStates) {
    const { column, sortOrder } = sorterStates;
    return { column, order: sortOrder, field: column.dataIndex, columnKey: column.key };
}
function generateSorterInfo(sorterStates) {
    const list = sorterStates.filter(({ sortOrder }) => sortOrder).map(stateToInfo);
    if (list.length === 0 && sorterStates.length) {
        return Object.assign(Object.assign({}, stateToInfo(sorterStates[sorterStates.length - 1])), { column: undefined });
    }
    if (list.length <= 1) {
        return list[0] || {};
    }
    return list;
}
export function getSortData(data, sortStates, childrenColumnName) {
    const innerSorterStates = sortStates
        .slice()
        .sort((a, b) => b.multiplePriority - a.multiplePriority);
    const cloneData = data.slice();
    const runningSorters = innerSorterStates.filter(({ column: { sorter }, sortOrder }) => getSortFunction(sorter) && sortOrder);
    // Skip if no sorter needed
    if (!runningSorters.length) {
        return cloneData;
    }
    return cloneData
        .sort((record1, record2) => {
        for (let i = 0; i < runningSorters.length; i += 1) {
            const sorterState = runningSorters[i];
            const { column: { sorter }, sortOrder } = sorterState;
            const compareFn = getSortFunction(sorter);
            if (compareFn && sortOrder) {
                const compareResult = compareFn(record1, record2, sortOrder);
                if (compareResult !== 0) {
                    return sortOrder === ASCEND ? compareResult : -compareResult;
                }
            }
        }
        return 0;
    })
        .map(record => {
        const subRecords = record[childrenColumnName];
        if (subRecords) {
            return Object.assign(Object.assign({}, record), { [childrenColumnName]: getSortData(subRecords, sortStates, childrenColumnName) });
        }
        return record;
    });
}
export default function useFilterSorter({ prefixCls, mergedColumns, onSorterChange, sortDirections, tableLocale, showSorterTooltip }) {
    const [sortStates, setSortStates] = React.useState(collectSortStates(mergedColumns, true));
    const mergedSorterStates = React.useMemo(() => {
        let validate = true;
        const collectedStates = collectSortStates(mergedColumns, false);
        // Return if not controlled
        if (!collectedStates.length) {
            return sortStates;
        }
        const validateStates = [];
        function patchStates(state) {
            if (validate) {
                validateStates.push(state);
            }
            else {
                validateStates.push(Object.assign(Object.assign({}, state), { sortOrder: null }));
            }
        }
        let multipleMode = null;
        collectedStates.forEach(state => {
            if (multipleMode === null) {
                patchStates(state);
                if (state.sortOrder) {
                    if (state.multiplePriority === false) {
                        validate = false;
                    }
                    else {
                        multipleMode = true;
                    }
                }
            }
            else if (multipleMode && state.multiplePriority !== false) {
                patchStates(state);
            }
            else {
                validate = false;
                patchStates(state);
            }
        });
        return validateStates;
    }, [mergedColumns, sortStates]);
    // Get render columns title required props
    const columnTitleSorterProps = React.useMemo(() => {
        const sortColumns = mergedSorterStates.map(({ column, sortOrder }) => ({
            column,
            order: sortOrder
        }));
        return {
            sortColumns,
            // Legacy
            sortColumn: sortColumns[0] && sortColumns[0].column,
            sortOrder: sortColumns[0] && sortColumns[0].order
        };
    }, [mergedSorterStates]);
    function triggerSorter(sortState) {
        let newSorterStates;
        if (sortState.multiplePriority === false
            || !mergedSorterStates.length
            || mergedSorterStates[0].multiplePriority === false) {
            newSorterStates = [sortState];
        }
        else {
            newSorterStates = [
                ...mergedSorterStates.filter(({ key }) => key !== sortState.key),
                sortState
            ];
        }
        setSortStates(newSorterStates);
        onSorterChange(generateSorterInfo(newSorterStates), newSorterStates);
    }
    const transformColumns = (innerColumns) => injectSorter(prefixCls, innerColumns, mergedSorterStates, triggerSorter, sortDirections, tableLocale, showSorterTooltip);
    const getSorters = () => generateSorterInfo(mergedSorterStates);
    return [transformColumns, mergedSorterStates, columnTitleSorterProps, getSorters];
}
